import { useRef, useState, type ComponentType } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ToneDropdownMenu } from './ToneDropdownMenu';

// Per-option styling for a 'toneDropdown' field (see DetailField's `kind`) -
// `value`/`label` mirror a select's option/optionLabel, `triggerClasses` is
// the full-tone look the trigger wears while this option is selected (see
// work-queue/priority/PrioritySelector.tsx's PRIORITY_BUTTON_TONES),
// `menuLabelClasses`/`menuIconClasses` are that option's own-tone row inside
// the popover. Kept generic (no risk-specific types) so any future select
// field could opt into the same chip-dropdown treatment - see
// fieldDefinitions.ts's RISK_STATUS_TONE_OPTIONS for the one real usage today.
export interface PracticeFieldToneOption {
    value: string;
    label: string;
    icon?: ComponentType<{ className?: string }>;
    triggerClasses: string;
    /** Icon color while this option is the trigger's current selection (e.g. white against a filled background). */
    triggerIconClasses?: string;
    menuLabelClasses: string;
    /** Icon color for this option's row inside the popover - muted, unlike the trigger's filled version. */
    menuIconClasses?: string;
    /** Marks this option as the field's unset/default state - skips the trigger's emphasized (bold) styling. */
    neutral?: boolean;
}

interface ToneDropdownFieldProps {
    id?: string;
    options: PracticeFieldToneOption[];
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}

// Neutral placeholder chip worn by the trigger when `value` matches no
// option (e.g. a not-yet-set field) - kept visually distinct from every real
// option's tone so an unset value can never be mistaken for a selection.
const UNSET_TONE = {
    label: 'Select…',
    triggerClasses: 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50'
};

/**
 * Chip-trigger + popover dropdown for a select-backed field, mirroring
 * work-queue/priority's PrioritySelector + PriorityMenu: the trigger wears
 * the current option's full tone (background/border/text) instead of nesting
 * a badge inside a neutral select, and opens a popover where every option is
 * rendered in its own tone with a check on the current one. Selecting an
 * option only calls `onChange` - no immediate PATCH like PrioritySelector's -
 * the Details tab's own Save/Discard bar commits the value like every other
 * field here.
 */
export function ToneDropdownField({
    id,
    options,
    value,
    disabled,
    onChange
}: ToneDropdownFieldProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const current = options.find((option) => option.value === value);
    const CurrentIcon = current?.icon;
    const trigger = current ?? UNSET_TONE;

    const handleSelect = (next: string) => {
        setMenuOpen(false);
        onChange(next);
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                id={id}
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
                className={`border rounded-lg px-2.5 h-9 min-w-[140px] inline-flex items-center justify-between gap-1.5 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    !current || current.neutral ? '' : 'font-semibold'
                } ${trigger.triggerClasses}`}
            >
                <span className="inline-flex items-center gap-1.5">
                    {CurrentIcon && (
                        <CurrentIcon
                            className={`h-3.5 w-3.5 ${current?.triggerIconClasses ?? ''}`}
                            aria-hidden="true"
                        />
                    )}
                    {trigger.label}
                </span>
                <ChevronDownIcon
                    className={`h-3.5 w-3.5 flex-shrink-0 ${current?.triggerIconClasses ?? 'text-gray-400'}`}
                    aria-hidden="true"
                />
            </button>
            {menuOpen && !disabled && (
                <ToneDropdownMenu
                    options={options}
                    current={value}
                    containerRef={containerRef}
                    onSelect={handleSelect}
                    onClose={() => setMenuOpen(false)}
                />
            )}
        </div>
    );
}
